import React, { useCallback, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './CodPincodeRiskPage.css';

function formatInteger(value) {
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(value || 0));
}

function formatCurrency(value) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return '-';
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return String(value).slice(0, 10);
  return new Date(ms).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function statusLabel(status) {
  if (status === 'block') return 'Block';
  if (status === 'watch') return 'Watch';
  return 'Safe';
}

export default function CodPincodeRiskPage() {
  const { currentUser } = useAuth();
  const [thresholds, setThresholds] = useState({
    blockMinCodOrders: 5,
    blockMinRtoRatePct: 40,
    watchMinCodOrders: 3,
    watchMinRtoRatePct: 30,
  });
  const [report, setReport] = useState(null);
  const [filter, setFilter] = useState('block');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const authedFetch = useCallback(async (url, options = {}) => {
    if (!currentUser) throw new Error('User not authenticated.');
    const token = await currentUser.getIdToken();
    const base = String(process.env.REACT_APP_API_BASE_URL || '').trim();
    const resolvedUrl = (base && typeof url === 'string' && url.startsWith('/')) ? `${base}${url}` : url;
    const headers = {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    };
    return fetch(resolvedUrl, { ...options, headers });
  }, [currentUser]);

  const calculateReport = useCallback(async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const params = new URLSearchParams();
      Object.entries(thresholds).forEach(([key, value]) => params.set(key, String(value)));
      const response = await authedFetch(`/api/v1/admin/cod-pincode-exclusion/risk-report?${params.toString()}`);
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to calculate COD pincode risk report.');
      }
      setReport(data.report || null);
      setFilter('block');
      const count = Number(data?.report?.summary?.blockPincodeCount || 0);
      setMessage(`Calculated ${formatInteger(count)} block pincode${count === 1 ? '' : 's'}.`);
    } catch (err) {
      setError(err.message || 'Failed to calculate report.');
    } finally {
      setLoading(false);
    }
  }, [authedFetch, thresholds]);

  const updateThreshold = useCallback((field, value) => {
    const n = Number(value);
    setThresholds((prev) => ({
      ...prev,
      [field]: Number.isFinite(n) ? n : prev[field],
    }));
  }, []);

  const filteredRows = useMemo(() => {
    const rows = Array.isArray(report?.rows) ? report.rows : [];
    if (filter === 'all') return rows;
    return rows.filter((row) => row.recommendation === filter);
  }, [filter, report]);

  const blockJson = useMemo(() => JSON.stringify({
    pincodes: Array.isArray(report?.blockPincodes) ? report.blockPincodes : [],
  }), [report]);

  const downloadBlockJson = useCallback(() => {
    const blob = new Blob([blockJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cod-block-pincodes-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [blockJson]);

  const copyBlockJson = useCallback(async () => {
    await navigator.clipboard.writeText(blockJson);
    setMessage('Copied block pincode JSON.');
  }, [blockJson]);

  return (
    <div className="cod-risk-page">
      <div className="cod-risk-header">
        <div>
          <h1>COD Pincode Risk</h1>
          <p>Monthly all-order analysis for Cash on Delivery pincode blocking.</p>
        </div>
        <button className="cod-risk-primary" type="button" onClick={calculateReport} disabled={loading}>
          {loading ? 'Calculating...' : 'Calculate Report'}
        </button>
      </div>

      <div className="cod-risk-controls">
        <label>
          <span>Block min COD orders</span>
          <input type="number" min="1" value={thresholds.blockMinCodOrders} onChange={(e) => updateThreshold('blockMinCodOrders', e.target.value)} />
        </label>
        <label>
          <span>Block min RTO %</span>
          <input type="number" min="1" max="100" value={thresholds.blockMinRtoRatePct} onChange={(e) => updateThreshold('blockMinRtoRatePct', e.target.value)} />
        </label>
        <label>
          <span>Watch min COD orders</span>
          <input type="number" min="1" value={thresholds.watchMinCodOrders} onChange={(e) => updateThreshold('watchMinCodOrders', e.target.value)} />
        </label>
        <label>
          <span>Watch min RTO %</span>
          <input type="number" min="1" max="100" value={thresholds.watchMinRtoRatePct} onChange={(e) => updateThreshold('watchMinRtoRatePct', e.target.value)} />
        </label>
      </div>

      {error && <div className="cod-risk-alert cod-risk-alert-error">{error}</div>}
      {message && <div className="cod-risk-alert cod-risk-alert-info">{message}</div>}

      {report && (
        <>
          <div className="cod-risk-summary">
            <div>
              <span>Pincodes</span>
              <strong>{formatInteger(report.summary?.pincodes)}</strong>
            </div>
            <div>
              <span>COD Orders</span>
              <strong>{formatInteger(report.summary?.codOrders)}</strong>
            </div>
            <div>
              <span>RTO Orders</span>
              <strong>{formatInteger(report.summary?.rtoOrders)}</strong>
            </div>
            <div>
              <span>RTO Rate</span>
              <strong>{Number(report.summary?.rtoRatePct || 0).toFixed(2)}%</strong>
            </div>
            <div>
              <span>Block List</span>
              <strong>{formatInteger(report.summary?.blockPincodeCount)}</strong>
            </div>
            <div>
              <span>Watchlist</span>
              <strong>{formatInteger(report.summary?.watchPincodeCount)}</strong>
            </div>
          </div>

          {Array.isArray(report.warnings) && report.warnings.length > 0 && (
            <div className="cod-risk-alert cod-risk-alert-warning">
              {report.warnings.map((warning) => <p key={warning}>{warning}</p>)}
            </div>
          )}

          <div className="cod-risk-download">
            <div>
              <span>Download format</span>
              <code>{blockJson}</code>
            </div>
            <div className="cod-risk-download-actions">
              <button type="button" onClick={copyBlockJson}>Copy JSON</button>
              <button type="button" onClick={downloadBlockJson}>Download JSON</button>
            </div>
          </div>

          <div className="cod-risk-sources">
            {(report.sources || []).map((source) => (
              <div key={source.id}>
                <span>{source.id}</span>
                <strong className={source.status === 'ok' ? 'is-ok' : 'is-failed'}>{source.status}</strong>
                {source.pincodeRows !== undefined && <small>{formatInteger(source.pincodeRows)} pincode rows</small>}
              </div>
            ))}
          </div>

          <div className="cod-risk-table-toolbar">
            <div className="cod-risk-tabs">
              {['block', 'watch', 'safe', 'all'].map((item) => (
                <button key={item} type="button" className={filter === item ? 'is-active' : ''} onClick={() => setFilter(item)}>
                  {item === 'all' ? 'All' : statusLabel(item)}
                </button>
              ))}
            </div>
            <span>{formatInteger(filteredRows.length)} rows</span>
          </div>

          <div className="cod-risk-table-wrap">
            <table className="cod-risk-table">
              <thead>
                <tr>
                  <th>Pincode</th>
                  <th>Action</th>
                  <th>COD</th>
                  <th>RTO</th>
                  <th>RTO %</th>
                  <th>RTO Value</th>
                  <th>Latest RTO</th>
                  <th>Location</th>
                  <th>Sources</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.slice(0, 500).map((row) => (
                  <tr key={row.pincode}>
                    <td><strong>{row.pincode}</strong></td>
                    <td><span className={`cod-risk-pill is-${row.recommendation}`}>{statusLabel(row.recommendation)}</span></td>
                    <td>{formatInteger(row.codOrders)}</td>
                    <td>{formatInteger(row.rtoOrders)}</td>
                    <td>{Number(row.rtoRatePct || 0).toFixed(2)}%</td>
                    <td>{formatCurrency(row.rtoValue)}</td>
                    <td>{formatDate(row.latestRtoDate)}</td>
                    <td>{[...(row.cities || []), ...(row.states || [])].slice(0, 2).join(', ') || '-'}</td>
                    <td>{Object.entries(row.sources || {}).map(([key, value]) => `${key}: ${value}`).join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
